import { isEqual } from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import { FIELD_DEF_LIST_CONSTANTS, NON_NUMERIC_FC, eventVersionExtractor } from '../../constants';
import { IFieldDefRowItem, IPatternDefinition, IPatternRegistryValidationModel, PatternRegistryFormData } from '../../models';
import classes from '../../modules/sources/source-registry/pattern-registry-form/PatternRegistryForm.module.scss';

/**
 * @method updateFormData
 * @description Updates the form data with the provided error and validation status.
 * @param previousData - The previous form data to be updated.
 * @param error - The error message to be set.
 * @param isValidStatus - The validation status to be set.
 * @returns The updated form data.
 */
export const updateFormData = (previousData: IPatternRegistryValidationModel, error: string, isValidStatus: boolean) => {
  const newFormData = { ...previousData };
  newFormData.patternRegistryName.error = error;
  newFormData.patternRegistryName.isValid = isValidStatus;
  return newFormData;
};

/**
 * @method generateFieldConditions
 * @description generates field conditions with dynamic keys
 */
const generateFieldConditions = (fdData: IFieldDefRowItem[]) => {
  const finalObj = fdData.map((item) => {
    const contentRef = {
      path: [item.name],
      contentType: item.contentType,
      conversionPattern: item.conversionPattern,
      required: item.required
    };
    const dynamicKey = {
      contentRef,
      operator: item.operator,
      value: item.value
    };
    return dynamicKey;
  });
   
  const result: any = {};
   
  finalObj.forEach((ele: any, index: number) => {
    result[`f${index + 1}`] = ele;
  });
  return result;
};

/**
 * @method duplicateNameChecker
 * @description Checks for duplicate names in the given array and updates the form data and error state accordingly.
 * @param duplicateNameValue - The array of items to check for duplicate names.
 * @param formData - The form data object containing the field definition name.
 * @param setDuplicateError - A function to set the duplicate error message state.
 * @param setFormParamValues - A function to set the form parameter values state.
 * @param errorMessage - The error message to display when duplicate names are found.
 */
export const duplicateNameChecker = (duplicateNameValue: IFieldDefRowItem[], formData: IPatternRegistryValidationModel, setDuplicateError: (value: SetStateAction<string | undefined>) => void, setFormParamValues: (value: SetStateAction<PatternRegistryFormData>) => void, errorMessage: string) => {
  if (duplicateNameValue.length > 0) {
    formData.fieldDefName.isTouched = true;
    formData.fieldDefName.isValid = false;
    setDuplicateError(errorMessage);
    setFormParamValues(formData);
  } else {
    setDuplicateError('');
  }
};

/**
 * @method fieldDefClickHandler
 * @description Handles the click event for the field definition.
 * If the current mode is edit mode, it clears the value of the field.
 * @param isEditFD - A boolean indicating whether the field definition is in edit mode.
 * @param isEditPatternMode - A boolean indicating whether the pattern mode is in edit mode.
 * @param formData - The form data containing the field values.
 */
export const fieldDefClickHandler = (isEditFD: boolean, isEditPatternMode: boolean, formData: IPatternRegistryValidationModel) => {
  if (getCurrentMode(isEditFD, isEditPatternMode)) {
    formData.fieldN.value = '';
  }
};

/**
 * @method conversionPatternChange
 * @description Handles the change event for the conversion pattern in the pattern registry.
 * If there are duplicate conversion pattern values, it marks the field as invalid and updates the form data.
 * Otherwise, it clears the duplicate error message.
 * @param duplicateConversionPatternValue - The array of duplicate conversion pattern values.
 * @param formData - The pattern registry validation model.
 * @param setFormParamValues - The function to update the form parameter values.
 * @param setDuplicateError - The function to set the duplicate error message.
 */
export const conversionPatternChange = (duplicateConversionPatternValue: IFieldDefRowItem[], formData: IPatternRegistryValidationModel, setFormParamValues: (value: SetStateAction<PatternRegistryFormData>) => void, setDuplicateError: (value: SetStateAction<string | undefined>) => void) => {
  if (duplicateConversionPatternValue.length > 0) {
    formData.fieldDefConversionPattern.isTouched = true;
    formData.fieldDefConversionPattern.isValid = false;
    setFormParamValues(formData);
  } else {
    setDuplicateError('');
  }
};

/**
 * @method displayCreate
 * @description function display create button
 * @param formData
 * @returns boolean
 */
export const displayCreate = (formData: IPatternRegistryValidationModel) => {
  const mandatoryField = [
    formData.patternRegistryName.isValid, formData.patternRegistryName.value !== '', formData.eventName.isValid, formData.eventName.value !== '', formData.fieldCondition.value !== '', formData.fieldCondition.isValid,
    formData.occurrences.value !== '', formData.occurrences.isValid
  ];
  if (formData.fieldCondition.value === 'AT_LEAST' || formData.fieldCondition.value === 'EXACTLY') {
    mandatoryField.push(formData.fieldN.value !== '');
    mandatoryField.push(formData.fieldN.isValid);
  }
  if (formData.occurrences.value !== '' && formData.occurrences.isValid && parseInt(formData.occurrences.value, 10) > 1) {
    mandatoryField.push(formData.timeToLive.value !== '');
    mandatoryField.push(formData.uniqueId.value !== '');
    mandatoryField.push(formData.timeToLive.isValid);
    mandatoryField.push(formData.uniqueId.isValid);
  }

  const shouldDisplay = mandatoryField.every((i) => i === true);
  return !shouldDisplay;
};

/**
 * @method clearFormDataDynamic
 * @description This is function to dynamically clear selective fields in formData
 * @param formObj: this a formData object obtained from hook
 * @param fieldNames: array of formData field keys
 * @returns updated formData object
 */
export const clearFormDataDynamic = (formObj: IPatternRegistryValidationModel, fieldNames: string[]) => {
  fieldNames.forEach((element) => {
    formObj[element as keyof typeof formObj].value = '';
    formObj[element as keyof typeof formObj].error = '';
    formObj[element as keyof typeof formObj].isValid = true;
    formObj[element as keyof typeof formObj].isTouched = false;
  });
};

/**
 * @method generateFdDataState
 * @description a function to reverse engineer response and populate fdData state
 * @param fdRespObj: response received from get call
 * @returns a state
 */
 
export const generateFdDataState = (fdRespObj: any) => {
  const fdDataArr: IFieldDefRowItem[] = [];
  const fdDataItem: IFieldDefRowItem = {
    required: false,
    id: '',
    name: '',
    operator: '',
    value: '',
    contentType: '',
    conversionPattern: ''
  };
  const getKeysArr = Object.keys(fdRespObj);
  if (getKeysArr.length > 0) {
    getKeysArr.forEach((ele, index) => {
      const updatedFdDataItem = { ...fdDataItem };
      const [name] = fdRespObj[ele as keyof typeof fdRespObj].contentRef.path;
      updatedFdDataItem.name = name;
      updatedFdDataItem.operator = fdRespObj[ele as keyof typeof fdRespObj].operator;
      updatedFdDataItem.value = fdRespObj[ele as keyof typeof fdRespObj].value;
      updatedFdDataItem.contentType = fdRespObj[ele as keyof typeof fdRespObj].contentRef.contentType;
      updatedFdDataItem.id = `fd-item-${Date.now()}-${name}-${index}`;
      updatedFdDataItem.conversionPattern = fdRespObj[ele as keyof typeof fdRespObj].contentRef.conversionPattern;
      updatedFdDataItem.required = fdRespObj[ele as keyof typeof fdRespObj].contentRef.required;
      fdDataArr.push(updatedFdDataItem);
    });
  }

  return fdDataArr;
};

/**
 * @method extractViewFormData
 * @description Fn to update form in view mode
 * @param viewDefinition
 * @param formData
 * @returns updated formData obj
 */
export const extractViewFormData = (viewDefinition: IPatternDefinition, formData: IPatternRegistryValidationModel) => {
  const viewFormData = { ...formData };
  if (viewDefinition) {
    viewFormData.patternRegistryName.value = viewDefinition.patternName;
    viewFormData.eventName.value = viewDefinition.eventName;
    viewFormData.eventName.value = viewDefinition.eventName;
    viewFormData.eventName.isTouched = false;
    viewFormData.fieldCondition.value = viewDefinition.patternDefinition.fieldConditionCombiners[0].combiner;
    viewFormData.fieldN.value = viewDefinition.patternDefinition.fieldConditionCombiners[0].n.toString();
    viewFormData.fieldDefContentType.value = ''; // keep this empty to prevent edit issues
    viewFormData.fieldDefName.value = ''; // keep this empty to prevent edit issues
    viewFormData.fieldDefOperator.value = ''; // keep this empty to prevent edit issues
    viewFormData.fieldDefValue.value = ''; // keep this empty to prevent edit issues
    if (viewDefinition.occurrences && viewDefinition.timeToLive && viewDefinition.uniqueId) {
      viewFormData.occurrences.value = viewDefinition.occurrences;
      viewFormData.timeToLive.value = viewDefinition.timeToLive;
      viewFormData.uniqueId.value = viewDefinition.uniqueId;
    } else {
      viewFormData.occurrences.value = '1';
    }
  }
  return viewFormData;
};

/**
 * @method createObjGenerator
 * @description function to generate create object for create pattern API
 * @param formData
 * @param fieldDefintnLiist
 * @returns request object
 */
export const createObjGenerator = (formData: IPatternRegistryValidationModel, fieldDefinitnList: IFieldDefRowItem[], eventVersion?: string | number | undefined, edit = false) => {
  const patternDefObj = generateFieldConditions(fieldDefinitnList);
  const combiners = Object.keys(patternDefObj);
  const [patternEventVal] = formData.eventName.value.split(' ');
  let wholeDefinition: IPatternDefinition = {
    patternName: formData.patternRegistryName.value,
    eventName: patternEventVal,
    eventVersion: (edit && eventVersion) ? eventVersion : 1,
    patternDefinition: {
      fieldConditions: patternDefObj,
      fieldConditionCombiners: [{
        fieldConditionRefs: combiners,
        combiner: formData.fieldCondition.value,
        n: formData.fieldCondition.value === 'ALL' || formData.fieldCondition.value === 'ANY' ? combiners.length : parseInt(formData.fieldN.value, 10)
      }]
    }
  };
  if (formData.occurrences.isValid && parseInt(formData.occurrences.value, 10) > 1 && formData.timeToLive.isValid && formData.uniqueId.isValid) {
    wholeDefinition = {
      ...wholeDefinition,
      occurrences: formData.occurrences.value,
      timeToLive: formData.timeToLive.value,
      uniqueId: formData.uniqueId.value
    };
  }

  return {
    name: formData.patternRegistryName.value,
    definition: wholeDefinition
  };
};
/**
 * @method formIsEmptyInEdit
 * @description Used to populate form in Edit mode on load
 * @param currentFormdataObj: a create object with current state of formData
 * @returns boolean
 */
export const formIsEmptyInEdit = (form: IPatternRegistryValidationModel) => {
  const isFormEmpty = [form.patternRegistryName.value, form.fieldCondition.value];
  if (isFormEmpty.every((x) => x === '')) {
    return true;
  }
  return false;
};

/**
 * @method comparePatternFormData
 * @description a function to enable or disable update button
 * it also helps to update formData when we land in Edit mode for first time
 * @param formData
 * @param fieldDefinitnList
 * @param journeyArray
 * @param viewResponse
 * @returns boolean
 */
export const comparePatternFormData = (formData: IPatternRegistryValidationModel, fieldDefinitnList: IFieldDefRowItem[], viewResponse: IPatternDefinition | undefined) => {
  /**
   * remove action key from definition in view response since it is not needed in formData comparison
   */
  const nonActionDefintion: any = viewResponse ?? undefined;
  if (nonActionDefintion && nonActionDefintion?.timetolive) { // viewResponse ttl is string but we need int for better comparison
    nonActionDefintion.timetolive = parseInt(nonActionDefintion.timetolive, 10); // convert into int before comparison
  }
  if (nonActionDefintion) {
    delete nonActionDefintion.action;
    delete nonActionDefintion.id;
  }
  // sort the object to resolve defect-111729
  const orderedAPI = nonActionDefintion ? Object.keys(nonActionDefintion).sort((a, b) => a.localeCompare(b)).reduce(
     
    (obj: any, key: any) => {
      obj[key] = nonActionDefintion[key];
      return obj;
    },
    {}
  ) : undefined;
  // since backend is return stringified event version we need to change to numeric value for comparision purposes
  if (orderedAPI && orderedAPI.eventVersion) {
    orderedAPI.eventVersion = Number(orderedAPI.eventVersion);
  }
  const apiResp = {
    // sourceName: viewResponse?.sourceName,
    definition: orderedAPI
  };
  // form a request object with updated formData to detect user edits
   
  let currentFormdataObj: { name?: string, definition: any } = createObjGenerator(formData, fieldDefinitnList, eventVersionExtractor(formData.eventName.value), true);

  /**
   * remove action key from definition in currentFormdataObj since it is not needed detect formData edits
   */
  const nonActionDefinitionCurrent: any = (currentFormdataObj);
  if (nonActionDefinitionCurrent?.timetolive) { // formdata ttl is string but we need int for better comparison
    nonActionDefinitionCurrent.timetolive = parseInt(nonActionDefinitionCurrent.timetolive, 10); // convert into int before comparison
  }
  if (nonActionDefinitionCurrent) {
    delete nonActionDefinitionCurrent.action;
    // sort the object to resolve defect-111729
    const orderedCurrent = Object.keys(nonActionDefinitionCurrent.definition).sort((a, b) => a.localeCompare(b)).reduce(
       
      (obj: any, key: any) => {
        obj[key] = nonActionDefinitionCurrent.definition[key];
        return obj;
      },
      {}
    );
    currentFormdataObj = {
      // sourceName: currentFormdataObj.sourceName,
      definition: orderedCurrent
    };
  }
  // when response is undefined keep update button disabled
  if (viewResponse === undefined) {
    return true;
  }
  /**
   * when any field is invalid while editing or no fieldDefinitnList exist, keep update button disabled
   * as it is a fault pattern should not be allowed to update
   */
  if (displayCreate(formData) || fieldDefinitnList.length < 1) {
    return true;
  }
  const currentFormdataString = JSON.stringify(currentFormdataObj);
  const previousDataString = JSON.stringify(apiResp);
  return isEqual(currentFormdataString, previousDataString);
};

/**
 * @method eventInvalidAndTouchedFn
 * @description Checks if the event name is invalid and has been touched.
 * @param formData - The form data containing the event name.
 * @returns A boolean value indicating if the event name is invalid and touched.
 */
export const eventInvalidAndTouchedFn = (formData: IPatternRegistryValidationModel) => (!formData.eventName.isValid && formData.eventName.isTouched);

/**
 * @method verifyErrorEvent
 * @description Verifies the error event based on the provided form data, event error message, and event loading status.
 * @param formData - The form data containing the event name validation model.
 * @param hasEventErrorMsg - The event error message.
 * @param isEventLoading - The event loading status.
 * @returns A boolean value indicating whether the error event is valid or not.
 */
export const verifyErrorEvent = (formData: IPatternRegistryValidationModel, hasEventErrorMsg: string, isEventLoading: boolean) =>
  (!formData.eventName.isValid || (hasEventErrorMsg && !isEventLoading));

/**
 * @method enableAdd
 * @description enable or disable add button as per form data
 * @returns boolean
 */
export const enableAdd = (formData: IPatternRegistryValidationModel, duplicateError: string | undefined) => {
  const { fieldDefName, fieldDefOperator, fieldDefValue, fieldDefContentType, fieldDefConversionPattern } = formData;
  const allFieldsValidity = [fieldDefName.isValid, fieldDefOperator.isValid, fieldDefValue.isValid, fieldDefContentType.isValid, fieldDefConversionPattern.isValid];
  const allFieldsRequired = [fieldDefName.value, fieldDefOperator.value, fieldDefValue.value, fieldDefContentType.value, fieldDefConversionPattern.value];
  const allNotEmpty = allFieldsRequired.every((i) => i !== '');
  const allValid = allFieldsValidity.every((i) => i === true);
  const canAdd = !(allNotEmpty && allValid && !duplicateError);
  return canAdd;
};

/**
 * @method enableClear
 * @description enable or disable clear button as per form data
 * @returns boolean
 */
export const enableClear = (formData: IPatternRegistryValidationModel) => {
  const { fieldDefName, fieldDefOperator, fieldDefValue, fieldDefContentType, fieldDefConversionPattern } = formData;
  const allFieldsRequired = [fieldDefName.value, fieldDefOperator.value, fieldDefValue.value, fieldDefContentType.value, fieldDefConversionPattern.value];
  const touchedFields = [fieldDefName.isTouched, fieldDefOperator.isTouched, fieldDefValue.isTouched, fieldDefContentType.isTouched, fieldDefConversionPattern.isTouched];
  const anyTouchedFields = touchedFields.some((i) => i === true);
  const allNotEmpty = allFieldsRequired.some((i) => i !== '');
  return (allNotEmpty !== true && anyTouchedFields !== true);
};

/**
 * @method getCurrentMode
 * @description FieldDefinitionComponentForm helper function to confirm non-edit modes
 * @param isEditFD boolean
 * @param isEditPatternMode boolean
 * @returns boolean
 */
const getCurrentMode = (isEditFD: boolean, isEditPatternMode: boolean) => !isEditFD && !isEditPatternMode;

/**
 * @method isPatternEditMode
 * @description verify edit mode
 * @param isViewPatternMode boolean
 * @param isEditPatternMode boolean
 * @returns boolean
 */
export const isPatternEditMode = (isViewPatternMode: boolean, isEditPatternMode: boolean) => !isViewPatternMode || isEditPatternMode;

/**
 * @method syncState
 * @description sync local states
 * @param operator string
 * @param contentType string
 * @param name string
 * @param setOperator func
 * @param setContentType func
 * @param setName func
 * @param formData object
 */
export const syncState = (
  {
    operator,
    contentType,
    name,
    conversionPattern,
    isReqd,
    setOperator,
    setContentType,
    setName,
    setConversionPattern,
    setIsReqd,
    formData
  }: {
    operator: string;
    contentType: string;
    name: string;
    conversionPattern: string;
    isReqd: boolean;
    setOperator: (value: string) => void;
    setContentType: (value: string) => void;
    setName: (value: string) => void;
    setConversionPattern: (value: string) => void;
    setIsReqd: (value: boolean) => void;
    formData: IPatternRegistryValidationModel;
  }
) => {
  if (operator !== formData.fieldDefOperator.value) {
    setOperator(formData.fieldDefOperator.value);
  }
  if (contentType !== formData.fieldDefContentType.value) {
    setContentType(formData.fieldDefContentType.value);
  }
  if (name !== formData.fieldDefName.value) {
    setName(formData.fieldDefName.value);
  }
  if (conversionPattern !== formData.fieldDefConversionPattern.value) {
    setConversionPattern(formData.fieldDefConversionPattern.value);
  }

  if (isReqd !== formData.fieldDefIsRequired.value) {
    setIsReqd(formData.fieldDefIsRequired.value);
  }
};

/**
 * @method numericFieldCondition
 * @description lets us know if the field condition accepts numeric value
 */
const numericFieldCondition = (formData: IPatternRegistryValidationModel) => formData.fieldCondition.value === FIELD_DEF_LIST_CONSTANTS.EXACTLY || formData.fieldCondition.value === FIELD_DEF_LIST_CONSTANTS.AT_LEAST;

/**
 * @method isFieldNGreaterHandler
 * @description function to determine if fieldN is more than data length USED IN fieldNChangeHandler
 * @param e event
 * @param formData object
 * @param data list data
 * @returns boolean
 */
export const isFieldNGreaterHandler = (e: React.ChangeEvent<HTMLInputElement>, formData: IPatternRegistryValidationModel, data: IFieldDefRowItem[]) => ((numericFieldCondition(formData)) && e.target.value !== '' && parseInt(e.target.value, 10) > data.length);

/**
 * @method isFieldNGreaterUseEffect
 * @description function to determine if fieldN is more than data length USED IN useEffect
 * @param formData object
 * @param data list data
 * @returns boolean
*/
export const isFieldNGreaterUseEffect = (formData: IPatternRegistryValidationModel, data: IFieldDefRowItem[]) => ((numericFieldCondition(formData)) && formData.fieldN.value !== '' && parseInt(formData.fieldN.value, 10) > data.length);

/**
 * @method isFieldNGreaterSelectHandler
 * @description function to determine if fieldN is more than data length IN SelectHandler
 * @param e event
 * @param formData object
 * @param data list data
 * @returns boolean
 */
const isFieldNGreaterSelectHandler = (e: React.ChangeEvent<HTMLSelectElement>, formData: IPatternRegistryValidationModel, data: IFieldDefRowItem[]) => ((e.target.value === FIELD_DEF_LIST_CONSTANTS.EXACTLY || e.target.value === FIELD_DEF_LIST_CONSTANTS.AT_LEAST) && parseInt(formData.fieldN.value, 10) > data.length);

// cognitive complexity reducing fn
/**
 * @method handler
 * @description Handles the data and updates the form parameters based on the provided conditions.
 * @param data - An array of IFieldDefRowItem representing the new data.
 * @param oldData - An array of IFieldDefRowItem representing the previous data.
 * @param setFormParamValues - A function to update the form parameter values.
 * @param setNoOfTimes - A function to set the value of 'noOfTimes'.
 * @param setValueError - A function to set the value of 'valueError'.
 * @param errString - A string representing the error message.
 */
export const handler = (data: IFieldDefRowItem[], oldData: IFieldDefRowItem[], setFormParamValues: Dispatch<SetStateAction<PatternRegistryFormData>>, setNoOfTimes: (value: string) => void, setValueError: (value: string) => void, errString: string) => {
  if (data.length !== oldData.length) {
    setFormParamValues((previousData) => {
      const newFormData = structuredClone(previousData);
      newFormData.fieldN.isValid = true;
      newFormData.fieldN.value = '';
      newFormData.fieldN.error = '';
      return newFormData;
    });
    setNoOfTimes('');
    setValueError('');
  } else {
    setFormParamValues((previousData) => {
      const newFormData = structuredClone(previousData);
      newFormData.fieldN.isTouched = true;
      newFormData.fieldN.isValid = false;
      newFormData.fieldN.error = '';
      return newFormData;
    });
    setValueError(errString);
  }
};

// used in useEffect
/**
 * @method pageSync
 * @description Synchronizes the page number with the data length and item count.
 * @param data - The array of field definitions.
 * @param onPageNoUpdate - The callback function to update the page number.
 * @param item - The number of items per page.
 * @param page - The current page number.
 */
export const pageSync = (data: IFieldDefRowItem[], onPageNoUpdate: (page: number) => void, item: number, page: number) => {
  if ((page + 1) > Math.ceil(data.length / item) && page) {
    onPageNoUpdate(Math.ceil(data.length / item) - 1);
  }
};

// used in useEffect
/**
 * @method pageFieldDefListSync
 * @description Synchronizes the page field definition list with the provided data.
 * If the length of the oldData and listData arrays are different and isView is false,
 * it updates the oldData array with the listData array.
 * @param oldData - The previous array of field definition row items.
 * @param listData - The new array of field definition row items.
 * @param isView - A boolean indicating whether the operation is in view mode.
 * @param setOldData - A function to update the oldData array.
 */
export const pageFieldDefListSync = (oldData: IFieldDefRowItem[], listData: IFieldDefRowItem[], isView: boolean, setOldData: (arr: IFieldDefRowItem[]) => void) => {
  if (oldData.length !== listData.length && !isView) { // don't do this in view mode
    setOldData([...listData]);
  }
};

// used in useEffect
/**
 * @method updateCurrentPageList
 * @description Updates the current page list based on the provided data, item, page, and setListData function.
 * If the data length is greater than the item, it filters the data based on the current page and item,
 * and updates the list data using the setListData function. Otherwise, it sets the list data to the entire data array.
 * @param data - The array of IFieldDefRowItem objects.
 * @param item - The number of items per page.
 * @param page - The current page number.
 * @param setListData - The function to update the list data.
 */
export const updateCurrentPageList = (data: IFieldDefRowItem[], item: number, page: number, setListData: (arr: IFieldDefRowItem[]) => void) => {
  if (data.length > item) {
    const filterList = data.filter((listItem, index) => (page * item) <= index && index < (page * item) + item);
    setListData([...filterList]);
  } else {
    setListData([...data]);
  }
};

/**
 * @method fieldNHandlerOnSelect
 * @description to manipulate fieldN when field condition is selected
 */
export const fieldNHandlerOnSelect = (e: React.ChangeEvent<HTMLSelectElement>, setFormParamValues: Dispatch<SetStateAction<PatternRegistryFormData>>, formData: IPatternRegistryValidationModel, data: IFieldDefRowItem[], setValueError: (value: string) => void, errString: string) => {
  const fieldnError = e.target.value === FIELD_DEF_LIST_CONSTANTS.ALL || e.target.value === FIELD_DEF_LIST_CONSTANTS.ANY ? '' : errString;
  // clear FieldN(# no. of times) when field confition is of non-numeric type
  if (e.target.value === FIELD_DEF_LIST_CONSTANTS.ALL || e.target.value === FIELD_DEF_LIST_CONSTANTS.ANY) {
    setFormParamValues((previousData) => {
      const newFormData = structuredClone(previousData);
      newFormData.fieldN.isValid = true;
      newFormData.fieldN.value = '';
      newFormData.fieldN.error = '';
      return newFormData;
    });
    setValueError(fieldnError);
  }
  // value should not be greater than data length
  if (isFieldNGreaterSelectHandler(e, formData, data)) {
    setFormParamValues((previousData) => {
      const newFormData = structuredClone(previousData);
      newFormData.fieldN.isTouched = true;
      newFormData.fieldN.isValid = false;
      return newFormData;
    });
    setValueError(formData.fieldN.value !== '' ? fieldnError : '');
  }
};

/**
 * @method setFieldNInView
 * @description set field N in view mode in state
 * @param formData object
 * @param setNoOfTimes string
 * @param isView boolean
 */
export const setFieldNInView = (formData: IPatternRegistryValidationModel, setNoOfTimes: (value: string) => void, isView: boolean) => {
  if (formData.fieldN.value !== '' && formData.fieldN.isValid && isView) {
    setNoOfTimes(formData.fieldN.value.toString());
  }
};

/**
 * @method fieldNErrorRetainer
 * @description updates fieldN in formData, usage in field definition useEffect
 * @param valueError string
 * @param noOfTimes string
 * @param isView boolean
 * @param setFormParamValues function
 */
export const fieldNErrorRetainer = (valueError: string, noOfTimes: string, isView: boolean, setFormParamValues: Dispatch<SetStateAction<PatternRegistryFormData>>) => {
  if (valueError !== '' && noOfTimes !== '' && !isView) {
    setFormParamValues((previousData) => {
      const newFormData = structuredClone(previousData);
      newFormData.fieldN.isTouched = true;
      newFormData.fieldN.isValid = false;
      newFormData.fieldN.error = '';
      return newFormData;
    });
  }
};

/**
 * @method fieldNHideShow
 * @description show hide logic
 * @param fieldCondition string
 * @returns class object
 */
export const fieldNHideShow = (fieldCondition: string) => {
  const style = NON_NUMERIC_FC.includes(fieldCondition) ? classes['pr-div-hidden'] : '';
  return style;
};
